import { DefaultPrimaryButton, InformationModal } from "@/styles/globalstyles";
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Modal,
  Radio,
  RadioGroup,
  Select,
  Typography,
} from "@mui/material";
import CloseButton from "../buttons/CloseButton";
import type {
  EditFirstTierSupplierPayload,
  FirstTierSupplier,
} from "@/api/supplierService";
import { useEffect, useState } from "react";
import FormulaBuilder from "./FormulaBuilder";

interface SaveFirstTierSupplierModalProps {
  open: boolean;
  onClose: () => void;
  selectedSupplier: FirstTierSupplier;
  handleSaveEdit: (
    supplierCode: string,
    payload: EditFirstTierSupplierPayload,
  ) => void;
}

const SaveFirstTierSupplierModal = ({
  open,
  onClose,
  selectedSupplier,
  handleSaveEdit,
}: SaveFirstTierSupplierModalProps) => {
  const [formData, setFormData] = useState<EditFirstTierSupplierPayload>({
    template_type: "None",
    is_admin_pct: false,
    is_profit_pct: false,
    admin_oem_formula: null,
    profit_oem_formula: null,
    admin_spare_formula: null,
    profit_spare_formula: null,
  });

  const oemVariables = [
    {
      label: "Sum Total Material Cost",
      value: "SUM_TOTAL_MATERIAL_COST",
      color: "#ffcdd2",
      hoverColor: "#ef9a9a",
    },
    {
      label: "Sum Child Part Process",
      value: "SUM_CHILD_PART_PROCESS",
      color: "#fff9c4",
      hoverColor: "#fff59d",
    },
    {
      label: "Sum OEM Part Process",
      value: "SUM_OEM_PART_PROCESS",
      color: "#ffe0b2",
      hoverColor: "#ffcc80",
    },
    {
      label: "Sum OEM Part Other Process",
      value: "SUM_OEM_PART_OTHER_PROCESS",
      color: "#c8e6c9",
      hoverColor: "#a5d6a7",
    },
  ];

  const spareVariables = [
    {
      label: "Sum Total Material Cost",
      value: "SUM_TOTAL_MATERIAL_COST",
      color: "#ffcdd2",
      hoverColor: "#ef9a9a",
    },
    {
      label: "Sum Child Part Process",
      value: "SUM_CHILD_PART_PROCESS",
      color: "#fff9c4",
      hoverColor: "#fff59d",
    },
    {
      label: "Sum OEM Part Process",
      value: "SUM_OEM_PART_PROCESS",
      color: "#ffe0b2",
      hoverColor: "#ffcc80",
    },
    {
      label: "Sum OEM Part Other Process",
      value: "SUM_OEM_PART_OTHER_PROCESS",
      color: "#c8e6c9",
      hoverColor: "#a5d6a7",
    },
    {
      label: "Sum OEM & Spare Part Process",
      value: "SUM_OEM_AND_SPARE_PART_PROCESS",
      color: "#D9C4A0",
      hoverColor: "#C8B28A",
    },
    {
      label: "Sum OEM & Spare Part Other Process",
      value: "SUM_OEM_AND_SPARE_PART_OTHER_PROCESS",
      color: "#A5C992",
      hoverColor: "#8FB87B",
    },
  ];

  useEffect(() => {
    if (open && selectedSupplier) {
      setFormData({
        template_type: selectedSupplier.template_type || "None",
        is_admin_pct: selectedSupplier.is_admin_pct || false,
        is_profit_pct: selectedSupplier.is_profit_pct || false,
        admin_oem_formula:
          selectedSupplier.admin_oem_formula ||
          "SUM_TOTAL_MATERIAL_COST + SUM_CHILD_PART_PROCESS + SUM_OEM_PART_PROCESS + SUM_OEM_PART_OTHER_PROCESS",
        profit_oem_formula:
          selectedSupplier.profit_oem_formula ||
          "SUM_TOTAL_MATERIAL_COST + SUM_CHILD_PART_PROCESS + SUM_OEM_PART_PROCESS + SUM_OEM_PART_OTHER_PROCESS",
        admin_spare_formula:
          selectedSupplier.admin_spare_formula ||
          "SUM_TOTAL_MATERIAL_COST + SUM_CHILD_PART_PROCESS + SUM_OEM_AND_SPARE_PART_PROCESS + SUM_OEM_AND_SPARE_PART_OTHER_PROCESS",
        profit_spare_formula:
          selectedSupplier.profit_spare_formula ||
          "SUM_TOTAL_MATERIAL_COST + SUM_CHILD_PART_PROCESS + SUM_OEM_AND_SPARE_PART_PROCESS + SUM_OEM_AND_SPARE_PART_OTHER_PROCESS",
      });
    }
  }, [selectedSupplier, open]);

  const handleClose = () => {
    setFormData({
      template_type: "None",
      is_admin_pct: false,
      is_profit_pct: false,
      admin_oem_formula: null,
      profit_oem_formula: null,
      admin_spare_formula: null,
      profit_spare_formula: null,
    });
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={{ ...InformationModal, maxHeight: "90vh", width: "80%" }}>
        <CloseButton onClick={handleClose} />
        <Typography variant="h2">แก้ไข First Tier Supplier</Typography>
        <Box p={1}>
          <Divider sx={{ my: 1 }} />
          <FormControl sx={{ mt: 1 }}>
            <FormLabel
              id="group-label"
              sx={{
                backgroundColor: "#fff",
                px: 0.5,
                color: "#666666",
                "&.Mui-focused": {
                  color: "primary.main",
                },
              }}
            >
              Template Type
            </FormLabel>
            <RadioGroup
              row
              aria-labelledby="template-type-label"
              value={formData?.template_type || selectedSupplier?.template_type}
              onChange={(e) => {
                const value = e.target.value as
                  | "Part"
                  | "Exchange Rate"
                  | "None";
                setFormData((prev) => ({
                  ...prev,
                  template_type: value,
                }));
              }}
            >
              <FormControlLabel
                value="Part"
                control={
                  <Radio
                    disabled={
                      selectedSupplier?.template_type !== "None" &&
                      selectedSupplier?.active_part > 0
                    }
                  />
                }
                label="Part"
              />
              <FormControlLabel
                value="Exchange Rate"
                control={
                  <Radio
                    disabled={
                      selectedSupplier?.template_type !== "None" &&
                      selectedSupplier?.active_part > 0
                    }
                  />
                }
                label="Exchange Rate"
              />
              <FormControlLabel
                value="None"
                control={
                  <Radio
                    disabled={
                      selectedSupplier?.template_type !== "None" &&
                      selectedSupplier?.active_part > 0
                    }
                  />
                }
                label="None"
              />
            </RadioGroup>
          </FormControl>
          {formData?.template_type === "Part" && (
            <Box mt={3}>
              <Typography
                variant="body1"
                fontSize="1.4rem"
                fontWeight="500"
                mb={2}
              >
                Calculate Part Price
              </Typography>
              <Box sx={{ display: "flex", gap: 2 }}>
                <FormControl sx={{ width: 120 }}>
                  <InputLabel
                    id="admin-label"
                    sx={{
                      backgroundColor: "#fff",
                      px: 0.5,
                      color: "#666666",
                      "&.Mui-focused": {
                        color: "primary.main",
                      },
                    }}
                  >
                    Admin Unit
                    <Typography component="span" color="error.main">
                      *
                    </Typography>
                  </InputLabel>
                  <Select
                    value={formData?.is_admin_pct}
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        is_admin_pct: e.target.value === "true",
                        flagged_reason: prev.flagged_reason
                          ? new Set([...prev.flagged_reason, "Admin Cost Unit"])
                          : new Set(["Admin Cost Unit"]),
                      }));
                    }}
                  >
                    <MenuItem value="true">Percentage</MenuItem>
                    <MenuItem value="false">THB/pcs</MenuItem>
                  </Select>
                </FormControl>
                <FormControl sx={{ width: 120 }}>
                  <InputLabel
                    id="profit-label"
                    sx={{
                      backgroundColor: "#fff",
                      px: 0.5,
                      color: "#666666",
                      "&.Mui-focused": {
                        color: "primary.main",
                      },
                    }}
                  >
                    Profit Unit
                    <Typography component="span" color="error.main">
                      *
                    </Typography>
                  </InputLabel>
                  <Select
                    value={formData?.is_profit_pct}
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        is_profit_pct: e.target.value === "true",
                        flagged_reason: prev.flagged_reason
                          ? new Set([
                              ...prev.flagged_reason,
                              "Profit Cost Unit",
                            ])
                          : new Set(["Profit Cost Unit"]),
                      }));
                    }}
                  >
                    <MenuItem value="true">Percentage</MenuItem>
                    <MenuItem value="false">THB/pcs</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box mt={2}>
                <Divider />
                <Typography
                  variant="body1"
                  fontSize="1.2rem"
                  fontWeight="400"
                  mt={2}
                  mb={1}
                >
                  Part OEM Price
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    alignItems: "center",
                    p: 2,
                    border: "1px solid #cccccc",
                    borderRadius: 1,
                    bgcolor: "#fafafa",
                  }}
                >
                  <Chip
                    label="Sum Total Material Cost"
                    sx={{ bgcolor: "#ffcdd2", fontWeight: 500 }}
                  />
                  <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>
                    +
                  </Typography>
                  <Chip
                    label="Sum Child Part Process"
                    sx={{ bgcolor: "#fff9c4", fontWeight: 500 }}
                  />
                  <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>
                    +
                  </Typography>
                  <Chip
                    label="Sum OEM Part Process"
                    sx={{ bgcolor: "#ffe0b2", fontWeight: 500 }}
                  />
                  <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>
                    +
                  </Typography>
                  <Chip
                    label="Sum OEM Part Other Process"
                    sx={{ bgcolor: "#c8e6c9", fontWeight: 500 }}
                  />
                  <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>
                    +
                  </Typography>
                  <Chip
                    label="Admin OEM Part Other Process"
                    sx={{ bgcolor: "#faafc2ff", fontWeight: 500 }}
                  />
                  <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>
                    +
                  </Typography>
                  <Chip
                    label="Profit OEM Part Other Process"
                    sx={{ bgcolor: "#b3e5fc", fontWeight: 500 }}
                  />
                </Box>
                {formData?.is_admin_pct && (
                  <FormulaBuilder
                    label="Admin OEM Part Other Process Equation"
                    value={formData?.admin_oem_formula}
                    onChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        admin_oem_formula: value,
                        flagged_reason: prev.flagged_reason
                          ? new Set([
                              ...prev.flagged_reason,
                              "Admin OEM Part Other Process Equation",
                            ])
                          : new Set(["Admin OEM Part Other Process Equation"]),
                      }))
                    }
                    variables={oemVariables}
                  />
                )}
                {formData?.is_profit_pct && (
                  <FormulaBuilder
                    label="Profit OEM Part Other Process Equation"
                    value={formData?.profit_oem_formula}
                    onChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        profit_oem_formula: value,
                        flagged_reason: prev.flagged_reason
                          ? new Set([
                              ...prev.flagged_reason,
                              "Profit OEM Part Other Process Equation",
                            ])
                          : new Set(["Profit OEM Part Other Process Equation"]),
                      }))
                    }
                    variables={oemVariables}
                  />
                )}
              </Box>
              <Box mt={2}>
                <Divider />
                <Typography
                  variant="body1"
                  fontSize="1.2rem"
                  fontWeight="400"
                  mt={2}
                  mb={1}
                >
                  Part Spare Price
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    overflowX: "auto",
                    gap: 1,
                    alignItems: "center",
                    p: 2,
                    border: "1px solid #cccccc",
                    borderRadius: 1,
                    bgcolor: "#fafafa",
                  }}
                >
                  <Chip
                    label="Sum Total Material Cost"
                    sx={{ bgcolor: "#ffcdd2", fontWeight: 500 }}
                  />
                  <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>
                    +
                  </Typography>
                  <Chip
                    label="Sum Child Part Process"
                    sx={{ bgcolor: "#fff9c4", fontWeight: 500 }}
                  />
                  <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>
                    +
                  </Typography>
                  <Chip
                    label="Sum OEM & Spare Part Process"
                    sx={{ bgcolor: "#D9C4A0", fontWeight: 500 }}
                  />
                  <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>
                    +
                  </Typography>
                  <Chip
                    label="Sum OEM & Spare Part Other Process"
                    sx={{ bgcolor: "#A5C992", fontWeight: 500 }}
                  />
                  <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>
                    +
                  </Typography>
                  <Chip
                    label="Admin Spare Part Other Process"
                    sx={{ bgcolor: "#F5B5FF", fontWeight: 500 }}
                  />
                  <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>
                    +
                  </Typography>
                  <Chip
                    label="Profit Spare Part Other Process"
                    sx={{ bgcolor: "#5FC0E0", fontWeight: 500 }}
                  />
                </Box>
                {formData?.is_admin_pct && (
                  <FormulaBuilder
                    label="Admin Spare Part Other Process Equation"
                    value={formData?.admin_spare_formula}
                    onChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        admin_spare_formula: value,
                        flagged_reason: prev.flagged_reason
                          ? new Set([
                              ...prev.flagged_reason,
                              "Admin Spare Part Other Process Equation",
                            ])
                          : new Set([
                              "Admin Spare Part Other Process Equation",
                            ]),
                      }))
                    }
                    variables={spareVariables}
                  />
                )}
                {formData?.is_profit_pct && (
                  <FormulaBuilder
                    label="Profit Spare Part Other Process Equation"
                    value={formData?.profit_spare_formula}
                    onChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        profit_spare_formula: value,
                        flagged_reason: prev.flagged_reason
                          ? new Set([
                              ...prev.flagged_reason,
                              "Profit Spare Part Other Process Equation",
                            ])
                          : new Set([
                              "Profit Spare Part Other Process Equation",
                            ]),
                      }))
                    }
                    variables={spareVariables}
                  />
                )}
              </Box>
            </Box>
          )}
        </Box>
        <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="contained"
            sx={DefaultPrimaryButton}
            onClick={() => {
              handleSaveEdit(selectedSupplier.supplier_code, formData!);
              handleClose();
            }}
          >
            บันทึก
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default SaveFirstTierSupplierModal;
